import { createSelector } from 'reselect';
import { CallbackSaga, createGeneralCall } from '@altermaind/core/requests-status-management';
import { IS_MOCK_ENABLED } from '@/_shared/constants/mock';
import { GenericGet } from '@/_shared/types';
import { GenericApiError } from '@/_shared/types/apiError';
import { getCurrentOrganization } from '@/core/state';
import { API_ORGANIZATIONS_ORGANIZATIONID_SURVEYS_CAMPAIGNS_INVITATION_TEMPLATES_TEMPLATEID_VERSIONID_PATH_KEY } from '@/swagger/constants/surveys-keys';
import {
  API_GET_SURVEYS_CAMPAIGN_INVITATION_BY_TEMPLATE_ID_CALL,
  KEY_GET_SURVEY_CAMPAIGN_INVITATION_BY_TEMPLATE_ID,
} from '../constats';
import { MOCK_RESPONSE_GET_SURVEY_CAMPAIGN_INVITATION_BY_TEMPLATE_ID } from '../mocks';

type RequestQueryGetSurveyCampaignInvitationByTemplateId = GenericGet<
  typeof API_ORGANIZATIONS_ORGANIZATIONID_SURVEYS_CAMPAIGNS_INVITATION_TEMPLATES_TEMPLATEID_VERSIONID_PATH_KEY
>['request']['query'];

type RequestGetSurveyCampaignInvitationByTemplateId = GenericGet<
  typeof API_ORGANIZATIONS_ORGANIZATIONID_SURVEYS_CAMPAIGNS_INVITATION_TEMPLATES_TEMPLATEID_VERSIONID_PATH_KEY
>['request']['path'];

export type ResponseGetSurveyCampaignInvitationByTemplateId = GenericGet<
  typeof API_ORGANIZATIONS_ORGANIZATIONID_SURVEYS_CAMPAIGNS_INVITATION_TEMPLATES_TEMPLATEID_VERSIONID_PATH_KEY
>['response']['body'];

type CallParamsGetSurveyCampaignInvitationByTemplateId =
  RequestQueryGetSurveyCampaignInvitationByTemplateId &
    Omit<RequestGetSurveyCampaignInvitationByTemplateId, 'organizationId'> & {
      versionId: string;
      successSaga?: CallbackSaga;
      errorSaga?: CallbackSaga;
    };

export type CallResponseGetSurveyCampaignInvitationByTemplateId =
  ResponseGetSurveyCampaignInvitationByTemplateId;

type CallErrorGetSurveyCampaignInvitationByTemplateId = GenericApiError;

type CallSelectorsGetSurveyCampaignInvitationByTemplateId = {
  departments: ResponseGetSurveyCampaignInvitationByTemplateId['departments'];
  allEmployees: { id: string; name: string; email: string; role?: string; department?: string }[];
};

export const getSurveyCampaignInvitationByTemplateIdCall = createGeneralCall<
  CallParamsGetSurveyCampaignInvitationByTemplateId,
  CallResponseGetSurveyCampaignInvitationByTemplateId,
  CallErrorGetSurveyCampaignInvitationByTemplateId,
  CallSelectorsGetSurveyCampaignInvitationByTemplateId
>({
  id: KEY_GET_SURVEY_CAMPAIGN_INVITATION_BY_TEMPLATE_ID,
  method: 'get',
  url:
    ({ payload }) =>
    state => {
      const org = getCurrentOrganization(state);
      return API_GET_SURVEYS_CAMPAIGN_INVITATION_BY_TEMPLATE_ID_CALL(
        org,
        payload.templateId,
        payload.versionId,
        payload.campaignId,
      );
    },
  successMock: IS_MOCK_ENABLED({ avoidMockOnDemoMode: true })
    ? MOCK_RESPONSE_GET_SURVEY_CAMPAIGN_INVITATION_BY_TEMPLATE_ID
    : undefined,
  selectors: {
    departments: ({ selectors }) =>
      createSelector(selectors.response, res => res?.departments || []),
    allEmployees: ({ selectors }) =>
      createSelector(selectors.departments, departments =>
        (departments ?? []).reduce(
          (acc, dept) => {
            if (dept.employees) {
              acc.push(
                ...dept.employees.map(emp => ({
                  id: emp.email || '',
                  name: `${emp.firstName} ${emp.lastName}`,
                  email: emp.email || '',
                  role: emp.role,
                  department: dept?.name && dept.name.toLowerCase() !== 'unknown' ? dept.name : '-',
                })),
              );
            }
            return acc;
          },
          [] as { id: string; name: string; email: string }[],
        ),
      ),
  },
});
